// src/component/Cart.js
import React, { useState, useEffect, useCallback, useContext } from 'react'; // Updated import to include useContext
import { FaShoppingCart, FaTrashAlt, FaSpinner, FaExclamationTriangle, FaArrowRight, FaPlus, FaMinus, FaCreditCard } from 'react-icons/fa';
import { toast } from 'react-toastify';

import apiClient from '../api/apiClient'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
import PaymentPopup from './PaymentPopup';
import { ChatContext } from '../context/ChatContext'; // Added import for ChatContext

const Cart = ({ isInline }) => { // Updated to accept isInline prop
    const { user } = useAuth();
    const { triggerDataRefresh } = useContext(ChatContext); // Added ChatContext usage
    const [cartItems, setCartItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRemoving, setIsRemoving] = useState(null);
    const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(null);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);

    const fetchCartItems = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/cart');
            const itemsWithNumericQuantity = (response.data || []).map(item => ({
                ...item,
                quantity: parseInt(item.quantity, 10) || 1
            }));
            setCartItems(itemsWithNumericQuantity);
            console.log("Cart Component: Fetched items:", itemsWithNumericQuantity);
        } catch (err) {
            console.error("Cart Component: Error fetching cart:", err);
            setError(err.response?.data?.error || err.message || 'Failed to load cart items.');
            setCartItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchCartItems();
    }, [fetchCartItems]);

    const handleRemoveItem = useCallback(async (orderItemId, serviceName) => {
        if (!orderItemId || isRemoving === orderItemId || isUpdatingQuantity === orderItemId) return;
        setIsRemoving(orderItemId);
        try {
            await apiClient.delete(`/cart/${orderItemId}`);
            toast.success(`"${serviceName}" removed from cart.`);
            setCartItems(prevItems => prevItems.filter(item => item.order_item_id !== orderItemId));
        } catch (err) {
            console.error(`Cart Component: Error removing item ${orderItemId}:`, err);
            toast.error(err.response?.data?.error || 'Failed to remove item.');
            fetchCartItems();
        } finally {
            setIsRemoving(null);
        }
    }, [isRemoving, isUpdatingQuantity, fetchCartItems]);

    const handleUpdateQuantity = useCallback(async (orderItemId, currentQuantity, change) => {
        const newQuantity = currentQuantity + change;
        if (newQuantity <= 0) {
            const itemToRemove = cartItems.find(item => item.order_item_id === orderItemId);
            if (itemToRemove) handleRemoveItem(orderItemId, itemToRemove.service_name);
            return;
        }
        if (isRemoving === orderItemId || isUpdatingQuantity === orderItemId) return;
        setIsUpdatingQuantity(orderItemId);
        try {
            console.log(`Cart Component: Updating item ${orderItemId} to quantity ${newQuantity}`);
            await apiClient.put(`/cart/${orderItemId}`, { quantity: newQuantity });
            toast.info(`Quantity updated for item.`);
            await fetchCartItems();
        } catch (err) {
            console.error(`Cart Component: Error updating quantity for item ${orderItemId}:`, err);
            toast.error(err.response?.data?.error || 'Failed to update quantity.');
            fetchCartItems();
        } finally {
            setIsUpdatingQuantity(null);
        }
    }, [cartItems, isRemoving, isUpdatingQuantity, handleRemoveItem, fetchCartItems]);

    const handleCheckout = () => {
        console.log("Cart Component: Proceed to Checkout clicked. Opening popup.");
        setCheckoutError(null);
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        if (isCheckingOut) return;
        console.log("Cart Component: Closing payment popup.");
        setIsPopupOpen(false);
        setCheckoutError(null);
    };

    const handleConfirmPayment = async () => {
        console.log("Cart Component: Confirm Payment clicked. Initiating API call.");
        setIsCheckingOut(true);
        setCheckoutError(null);
        try {
            console.log("Cart Component: Calling POST /api/checkout");
            const response = await apiClient.post('/checkout');
            console.log("Cart Component: Checkout API Success:", response.data);
            toast.success(response.data.message || 'Payment Successful! Order placed.');
            setIsPopupOpen(false);

            if (triggerDataRefresh) { // Trigger refresh using context
                triggerDataRefresh();
            }

            await fetchCartItems();
        } catch (err) {
            console.error("Cart Component: Checkout API Error:", err);
            const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || 'Payment failed. Please try again.';
            setCheckoutError(errorMessage);
            toast.error(`Checkout Failed: ${errorMessage}`);
        } finally {
            setIsCheckingOut(false);
        }
    };

    const totalPrice = cartItems.reduce((sum, item) => {
        const price = parseFloat(item.price);
        const quantity = parseInt(item.quantity, 10) || 0;
        return sum + (isNaN(price) ? 0 : price * quantity);
    }, 0);

    const renderLoading = () => ( <div style={styles.centeredMessage}><FaSpinner className="fa-spin" style={{ marginRight: '0.5rem' }} /> Loading cart...</div> );
    const renderError = () => ( <div style={styles.errorIndicator}><FaExclamationTriangle style={{ marginRight: '0.5rem' }} /> Error: {error}</div> );
    const renderEmptyCart = () => ( <div style={styles.centeredMessage}>Your cart is empty. Browse services to add items!</div> );

    return (
        <div style={styles.cartContainer}>
            <h2 style={styles.title}><FaShoppingCart style={{ marginRight: '10px' }}/> Your Shopping Cart</h2>

            {isLoading && renderLoading()}
            {!isLoading && error && renderError()}
            {!isLoading && !error && cartItems.length === 0 && renderEmptyCart()}

            {!isLoading && !error && cartItems.length > 0 && (
                <>
                    <ul style={styles.itemList}>
                        {cartItems.map((item) => {
                            const isLoadingThisItem = isRemoving === item.order_item_id || isUpdatingQuantity === item.order_item_id;
                            const isDecrementDisabled = isLoadingThisItem || item.quantity <= 1;
                            const isIncrementDisabled = isLoadingThisItem;

                            return (
                                <li key={item.order_item_id} style={styles.item}>
                                    <div style={styles.itemDetails}>
                                        <span style={styles.itemName}>{item.service_name}</span>
                                        <span style={styles.itemPrice}>
                                            {item.price > 0 ? `₹${parseFloat(item.price).toFixed(2)}` : 'Free'}
                                            {item.quantity > 1 && item.price > 0 && (
                                                <span style={styles.pricePerItem}> (₹{(parseFloat(item.price) * item.quantity).toFixed(2)} total)</span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={styles.itemControls}>
                                        <button
                                            style={{ ...styles.quantityButton, ...(isDecrementDisabled ? styles.quantityButtonDisabled : {}) }}
                                            onClick={() => handleUpdateQuantity(item.order_item_id, item.quantity, -1)}
                                            disabled={isDecrementDisabled}
                                            aria-label="Decrease quantity"
                                        >
                                            {isLoadingThisItem ? <FaSpinner className="fa-spin" size={12}/> : <FaMinus size={12} />}
                                        </button>
                                        <span style={styles.quantityDisplay}>{item.quantity}</span>
                                        <button
                                            style={{ ...styles.quantityButton, ...(isIncrementDisabled ? styles.quantityButtonDisabled : {}) }}
                                            onClick={() => handleUpdateQuantity(item.order_item_id, item.quantity, 1)}
                                            disabled={isIncrementDisabled}
                                            aria-label="Increase quantity"
                                        >
                                             {isLoadingThisItem ? <FaSpinner className="fa-spin" size={12}/> : <FaPlus size={12} />}
                                        </button>
                                        <button
                                            style={styles.removeButton}
                                            onClick={() => handleRemoveItem(item.order_item_id, item.service_name)}
                                            disabled={isLoadingThisItem}
                                            title="Remove Item"
                                        >
                                            {isRemoving === item.order_item_id ? <FaSpinner className="fa-spin" size={14} /> : <FaTrashAlt size={14} />}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <div style={styles.summary}>
                        <div style={styles.total}>
                            <span>Total:</span>
                            <span>₹{totalPrice.toFixed(2)}</span>
                        </div>
                        <button
                            style={{
                                ...styles.checkoutButton,
                                ...((cartItems.length === 0 || isCheckingOut) ? styles.checkoutButtonDisabled : {})
                            }}
                            onClick={handleCheckout}
                            disabled={cartItems.length === 0 || isCheckingOut}
                        >
                             Proceed to Checkout <FaArrowRight size={14} style={{ marginLeft: '8px' }} />
                        </button>
                    </div>
                </>
            )}

            {isPopupOpen && (
                <PaymentPopup
                    isOpen={isPopupOpen}
                    onClose={handleClosePopup}
                    totalAmount={totalPrice}
                    onConfirm={handleConfirmPayment}
                    isLoading={isCheckingOut}
                    error={checkoutError}
                />
            )}
        </div>
    );
};

const styles = {
    cartContainer: { padding: '20px', maxWidth: '800px', margin: '20px auto', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', },
    title: { display: 'flex', alignItems: 'center', color: '#1a237e', borderBottom: '2px solid #eee', paddingBottom: '15px', marginBottom: '20px', fontSize: '1.5em', },
    itemList: { listStyle: 'none', padding: 0, margin: 0, },
    item: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', borderBottom: '1px solid #f0f0f0', gap: '15px' },
    itemDetails: { display: 'flex', flexDirection: 'column', gap: '4px', flexGrow: 1 },
    itemName: { fontWeight: '500', color: '#333', },
    itemPrice: { color: '#555', fontSize: '0.95em', },
    pricePerItem: { color: '#777', fontSize: '0.85em', marginLeft: '5px'},
    itemControls: { display: 'flex', alignItems: 'center', gap: '8px', },
    quantityButton: {
        background: '#f0f2f9',
        border: '1px solid #e0e4ec',
        color: '#1a237e',
        cursor: 'pointer',
        padding: '0', width: '28px', height: '28px', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    quantityButtonHover: { backgroundColor: '#e0e4ec', },
    quantityButtonDisabled: { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#f5f5f5', },
    quantityDisplay: { minWidth: '30px', textAlign: 'center', fontWeight: '500', fontSize: '1.1em', color: '#333', padding: '0 5px', },
    removeButton: { background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', padding: '5px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s ease', marginLeft: '10px', },
    summary: { marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #eee', },
    total: { display: 'flex', justifyContent: 'space-between', fontSize: '1.2em', fontWeight: 'bold', marginBottom: '20px', color: '#1a237e', },
    checkoutButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px 20px', backgroundColor: '#1a237e', color: 'white', border: 'none', borderRadius: '6px', fontSize: '1em', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.3s ease', },
    checkoutButtonHover: { backgroundColor: '#0d1757', },
    checkoutButtonDisabled: { backgroundColor: '#ccc', cursor: 'not-allowed', opacity: 0.7 },
    centeredMessage: { textAlign: 'center', padding: '40px 20px', color: '#666', fontSize: '1.1em', },
    errorIndicator: { textAlign: 'center', padding: '20px', color: 'red', border: '1px solid red', borderRadius: '4px', backgroundColor: '#fff0f0', margin: '20px 0', },
    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' }, },
};

export default Cart;